import { $generateHtmlFromNodes } from '@lexical/html';
import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { InitialConfigType } from '@lexical/react/LexicalComposer';
import { HeadingNode } from '@lexical/rich-text';
import thlog from '@repo/utils/thlogger';
import { createEditor, EditorState, SerializedEditorState } from 'lexical';
import styles from '../TextEditor.module.css';
import { TextEditorState } from '../types';

const TEXT_EDITOR_LOCAL_STORAGE_KEY = 'text_editor_tmp';

export class TextEditorHandler {
  static #instance: TextEditorHandler;

  private constructor() {}

  static get instance() {
    if (!this.#instance) this.#instance = new TextEditorHandler();

    return this.#instance;
  }

  public getEditorStatesFromLocalStorage(): TextEditorState | null {
    if (!window) return null;

    const editorStates = localStorage.getItem(TEXT_EDITOR_LOCAL_STORAGE_KEY);

    if (!editorStates) return null;

    return JSON.parse(editorStates) as TextEditorState;
  }

  public getEditorStateFromLocalStorage(
    editorStateKey: keyof TextEditorState,
  ): TextEditorState[string] | null {
    if (!window) return null;

    const editorStates = this.getEditorStatesFromLocalStorage();

    if (!editorStates) return null;

    return editorStates[editorStateKey];
  }

  public setEditorStateInLocalStorage(
    editorStateKey: keyof TextEditorState,
    editorState: EditorState,
  ) {
    if (!window) return;

    const editorStates = this.getEditorStatesFromLocalStorage();

    localStorage.setItem(
      TEXT_EDITOR_LOCAL_STORAGE_KEY,
      JSON.stringify({
        ...(editorStates ?? {}),
        [editorStateKey]: {
          editorState: editorState.toJSON(),
          lastSavedAt: new Date().toISOString(),
        },
      }),
    );
  }

  public deleteEditorStateFromLocalStorage(
    editorStateKey: keyof TextEditorState,
  ) {
    if (!window) return;

    const editorStates = this.getEditorStatesFromLocalStorage();

    if (!editorStates) return;

    delete editorStates[editorStateKey];

    localStorage.setItem(
      TEXT_EDITOR_LOCAL_STORAGE_KEY,
      JSON.stringify(editorStates),
    );
  }

  public getTextEditorInitialConfig(
    editorStateKey: keyof TextEditorState,
    initialValue?: string,
  ): InitialConfigType {
    const theme = {
      paragraph: styles.paragraph,
      heading: {
        h1: styles.h1,
        h2: styles.h2,
        h3: styles.h3,
      },
      text: {
        bold: styles.textBold,
        italic: styles.textItalic,
        underline: styles.textUnderline,
        strikethrough: styles.textStrikethrough,
      },
      list: {
        ul: styles.ul,
        ol: styles.ol,
        listitem: styles.listitem,
      },
      link: styles.link,
    };

    const savedState = this.getEditorStateFromLocalStorage(editorStateKey);

    const stringifiedSavedState = savedState
      ? { editorState: JSON.stringify(savedState.editorState) }
      : { editorState: initialValue };

    return {
      namespace: 'TextEditor',
      theme,
      ...stringifiedSavedState,
      onError: error => thlog.error(error),
      nodes: [HeadingNode, ListNode, ListItemNode, LinkNode],
    };
  }

  public async handleSubmit(
    editorStateLocalStorageKey: string,
    onSubmit?: (editorState: string) => void | Promise<void>,
  ) {
    const serializedEditorState = this.getEditorStateFromLocalStorage(
      editorStateLocalStorageKey,
    );

    if (!serializedEditorState) return;

    if (onSubmit)
      await onSubmit(JSON.stringify(serializedEditorState.editorState));
  }

  public convertToMarkdown(
    serializedEditorState: SerializedEditorState,
  ): string {
    const editor = createEditor({
      namespace: 'MarkdownConverter',
      nodes: [HeadingNode, ListNode, ListItemNode, LinkNode],
      onError: error => thlog.error(error),
    });

    const editorState = editor.parseEditorState(serializedEditorState);
    let markdown = '';

    editorState.read(() => (markdown = $convertToMarkdownString(TRANSFORMERS)));

    return markdown;
  }

  public convertToHtml(serializedEditorState: SerializedEditorState): string {
    const editor = createEditor({
      namespace: 'HtmlConverter',
      nodes: [HeadingNode, ListNode, ListItemNode, LinkNode],
      onError: error => thlog.error(error),
    });

    const editorState = editor.parseEditorState(serializedEditorState);
    let html = '';

    editorState.read(() => (html = $generateHtmlFromNodes(editor)));

    return html;
  }
}
